import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Button, Card, EmptyState, Icon, SettingGroup, SettingRow, Sheet, Skeleton } from '@astral/ui';
import {
  ADMIN_USER_FILTERS,
  type AdminUserFilter,
  type AdminUserListItem,
  type Role,
  type UserDTO,
} from '@astral/shared';
import { openTelegramLink } from '@astral/telegram/webapp';
import { api, ApiClientError } from '../lib/api.js';
import { useAuth, useCurrentUser } from '../providers/AuthProvider.js';
import { useI18n, useT, type TranslationKey } from '../providers/I18nProvider.js';
import { SubHeader } from '../components/SubHeader.js';
import { useToast } from '../providers/ToastProvider.js';
import { RoleBadge } from '../components/RoleBadge.js';
import { formatDate } from '../lib/format.js';
import { telegramLink } from '../config.js';

/** Owner/Admin panel: compact member grid, attention badges, roles, premium, payments. */
export function AdminScreen(): JSX.Element {
  const t = useT();
  const { language } = useI18n();
  const me = useCurrentUser();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const canManageRoles = hasPermission('admin:manage_admins');
  const canManagePremium = hasPermission('admin:manage_premium');
  const canManagePayments = hasPermission('admin:manage_payments');

  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<AdminUserFilter>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdminUserListItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listUsers({ search: search.trim() || undefined, limit: 100 })
      .then((result) => !cancelled && setUsers(result.items))
      .catch((err: unknown) => !cancelled && setError(err instanceof ApiClientError ? err.message : 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [search, nonce]);

  const counts = useMemo(
    () => ({
      all: users.length,
      attention: users.filter((u) => u.attention === 'receipt_review').length,
      premium: users.filter((u) => u.isPremium).length,
      payments: users.filter((u) => u.pendingReceipt).length,
      admins: users.filter((u) => u.role === 'ADMIN' || u.role === 'OWNER').length,
    }),
    [users],
  );

  const shown = useMemo(() => {
    switch (filter) {
      case 'attention':
        return users.filter((u) => u.attention === 'receipt_review');
      case 'premium':
        return users.filter((u) => u.isPremium);
      case 'payments':
        return users.filter((u) => u.pendingReceipt);
      case 'admins':
        return users.filter((u) => u.role === 'ADMIN' || u.role === 'OWNER');
      default:
        return users;
    }
  }, [users, filter]);

  const applyUpdate = (updated: UserDTO): void => {
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
    setSelected((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  };
  const changeRole = (userId: string, role: Role): void => {
    setBusy(true);
    void api.setUserRole({ userId, role }).then(applyUpdate).catch(() => setNonce((n) => n + 1)).finally(() => setBusy(false));
  };
  const changePremium = (userId: string, isPremium: boolean): void => {
    setBusy(true);
    void api
      .setUserPremium({ userId, isPremium })
      .then((u) => {
        applyUpdate(u);
        toast.show(t('common.saved'), 'success');
      })
      .catch(() => setNonce((n) => n + 1))
      .finally(() => setBusy(false));
  };

  return (
    <div className="screen screen--with-nav">
      <SubHeader title={t('admin.title')} />
      <p className="muted" style={{ marginTop: -14 }}>{t('admin.subtitle')}</p>

      {canManagePayments && (
        <Card padding="md" className="admin-review border-glow" interactive onClick={() => navigate('/admin/payments')}>
          <div className="admin-review__icon">
            <Icon name="bell" size={18} />
            {counts.payments > 0 && <span className="admin-review__dot">{counts.payments}</span>}
          </div>
          <div className="home__create-text">
            <span className="home__create-title">{t('admin.reviewTitle')}</span>
            <span className="muted home__create-sub">
              {counts.payments > 0 ? t('admin.attentionReceipt') : t('admin.queueEmpty')}
            </span>
          </div>
          <Icon name="chevron-right" size={20} />
        </Card>
      )}

      <div className="searchbar">
        <Icon name="search" size={18} />
        <input value={search} placeholder={t('admin.searchPlaceholder')} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {/* Filter chips (wrap on narrow widths, never horizontal-scroll). */}
      <div className="admin-filters">
        {ADMIN_USER_FILTERS.map((f) => {
          const label = t(`admin.filter${f.charAt(0).toUpperCase()}${f.slice(1)}` as TranslationKey);
          const n = counts[f];
          return (
            <button
              key={f}
              className={`chip${filter === f ? ' chip--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {label}
              {(f === 'attention' || f === 'payments') && n > 0 && <span className="chip__count">{n}</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="admin-grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={132} radius={16} />
          ))}
        </div>
      ) : error ? (
        <EmptyState
          icon={<Icon name="shield" size={28} />}
          title={t('errors.loadFailed')}
          action={<Button onClick={() => setNonce((n) => n + 1)}>{t('common.retry')}</Button>}
        />
      ) : shown.length === 0 ? (
        <EmptyState icon={<Icon name="user" size={28} />} title={t('admin.noUsers')} />
      ) : (
        <>
          <div className="muted" style={{ fontSize: 13 }}>{t('admin.total', { count: counts.all })}</div>
          <div className="admin-grid">
            {shown.map((user) => (
              <button key={user.id} className="admin-card" onClick={() => setSelected(user)}>
                <div className="admin-card__avatar">
                  <Avatar name={user.firstName} src={user.photoUrl} size={52} />
                  {user.attention === 'receipt_review' && (
                    <span className="admin-card__badge admin-card__badge--alert" aria-label={t('admin.attentionReceipt')}>
                      <Icon name="bell" size={11} />
                    </span>
                  )}
                  {user.attention === 'premium_active' && (
                    <span className="admin-card__badge admin-card__badge--premium" aria-label={t('admin.premium')}>
                      <Icon name="sparkles" size={11} />
                    </span>
                  )}
                </div>
                <span className="admin-card__name">
                  {user.firstName}
                  {user.id === me.id ? ` · ${t('admin.you')}` : ''}
                </span>
                <span className="admin-card__handle">{user.username ? `@${user.username}` : t('admin.noProfile')}</span>
                <span className="admin-card__role">
                  <RoleBadge role={user.role} />
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* User detail */}
      <Sheet open={Boolean(selected)} onClose={() => setSelected(null)} title={t('admin.details')}>
        {selected && (
          <div className="stack" style={{ gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <Avatar name={selected.firstName} src={selected.photoUrl} size={56} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 17 }}>
                  {selected.firstName}
                  {selected.lastName ? ` ${selected.lastName}` : ''}
                </div>
                <div className="muted" style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selected.username ? `@${selected.username}` : t('admin.noProfile')}
                </div>
              </div>
            </div>

            {selected.pendingReceipt && canManagePayments && (
              <Button fullWidth leftIcon={<Icon name="bell" size={18} />} onClick={() => navigate('/admin/payments')}>
                {t('admin.viewReceipt')}
              </Button>
            )}

            <SettingGroup>
              <SettingRow title={t('settings.role')} trailing={<RoleBadge role={selected.role} />} />
              <SettingRow
                title={t('admin.premium')}
                subtitle={
                  selected.premiumExpiresAt ? t('admin.premiumUntil', { date: formatDate(selected.premiumExpiresAt, language) }) : undefined
                }
                trailing={<Badge tone={selected.isPremium ? 'accent' : 'neutral'}>{selected.isPremium ? t('usage.premium') : t('usage.free')}</Badge>}
              />
              <SettingRow
                title={t('admin.telegramId')}
                subtitle={selected.telegramId}
                trailing={<Icon name="copy" size={16} />}
                onClick={() => {
                  void navigator.clipboard?.writeText(selected.telegramId);
                  toast.show(t('common.copied'), 'success');
                }}
              />
              <SettingRow title={t('admin.memberSince', { date: formatDate(selected.createdAt, language) })} />
            </SettingGroup>

            <div className="stack">
              {selected.username && (
                <Button
                  variant="secondary"
                  fullWidth
                  leftIcon={<Icon name="user" size={18} />}
                  onClick={() => openTelegramLink(telegramLink(selected.username as string))}
                >
                  {t('admin.viewProfile')}
                </Button>
              )}

              {canManageRoles && selected.id !== me.id && selected.role !== 'OWNER' && (
                selected.role === 'USER' ? (
                  <Button fullWidth loading={busy} leftIcon={<Icon name="shield" size={18} />} onClick={() => changeRole(selected.id, 'ADMIN')}>
                    {t('admin.makeAdmin')}
                  </Button>
                ) : (
                  <Button variant="ghost" fullWidth loading={busy} onClick={() => changeRole(selected.id, 'USER')}>
                    {t('admin.removeAdmin')}
                  </Button>
                )
              )}

              {canManagePremium && selected.role !== 'OWNER' && (
                <Button
                  variant={selected.isPremium ? 'ghost' : 'secondary'}
                  fullWidth
                  loading={busy}
                  leftIcon={<Icon name="sparkles" size={18} />}
                  onClick={() => changePremium(selected.id, !selected.isPremium)}
                >
                  {selected.isPremium ? t('admin.revokePremium') : t('admin.grantPremium')}
                </Button>
              )}
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}
